import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import { useCities } from '@/common/hooks/queries/useCities'
import { useProducts } from '@/common/hooks/queries/useProducts'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { GeoTransformers } from '@/common/services/geoTransformers'
import { Mapper } from '@/common/services/mapper'
import { ProductData } from '@/common/types/product.type'
import { priceSurveyColumns } from '@/features/priceSurvey/constants/priceSurveyColumns.constant'
import { priceSurveyStatusOptions } from '@/features/priceSurvey/constants/priceSurveyStatusOptions.constant'
import { usePriceSurveys } from '@/features/priceSurvey/hooks/usePriceSurveys'
import { PriceSurveyTransformer } from '@/features/priceSurvey/services/priceSurveyTransformer'
import { DollarSignIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

const List = () => {
  const { filterParams, setFilterParams } = useFilterParams()

  const { date, formattedDate } = useFilterDate(filterParams)

  const [searchCity, setSearchCity] = useDebounce('')
  const [searchProduct, setSearchProduct] = useDebounce('')

  const { data: cityOptions } = useCities({
    filters: { search: searchCity, stateId: filterParams.state?.value },
    mapper: Mapper.mapWithTransformer({ transformer: GeoTransformers.optionsTransformer() }),
  })

  const { data: productOptions } = useProducts({
    filters: { search: searchProduct },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const {
    data: priceSurveys,
    meta,
    isPending: priceSurveysIsPending,
    error: priceSurveysError = {} as any,
    refetch: refetchPriceSurveys,
  } = usePriceSurveys({
    filters: {
      page: filterParams.page || 1,
      cityId: filterParams.city?.value,
      status: filterParams.status?.value,
      productId: filterParams.product?.value,
      surveyConductedAtGt: formattedDate.from,
      surveyConductedAtLt: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: PriceSurveyTransformer.tableTransformer({
        operationKlassToCheckPermission: 'Client::PriceSurveys::Show',
      }),
    }),
  })

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={priceSurveysError?.status}
      refetchFunction={refetchPriceSurveys}
    >
      <div className="flex flex-col gap-6">
        <ListHeader Icon={<DollarSignIcon className="w-10 h-10" strokeWidth={1.5} />} title="Pesquisas de preços" />

        <div className="flex items-center justify-between gap-4">
          <div className="flex gap-x-2">
            <ListFilters
              filters={[
                {
                  className: 'min-w-40',
                  placeholder: 'Cidade',
                  options: [{ label: 'Todas', value: '' }, ...cityOptions],
                  value: filterParams.city || '',
                  onChange: (e: any) => setFilterParams((prev) => ({ ...prev, city: e?.value ? e : null })),
                  onInputChange: setSearchCity,
                },
                {
                  className: 'min-w-40',
                  placeholder: 'Status',
                  options: [{ label: 'Todos', value: '' }, ...priceSurveyStatusOptions],
                  onChange: (e: any) => setFilterParams((prev) => ({ ...prev, status: e?.value ? e : null })),
                  value: filterParams.status,
                },
                {
                  className: 'min-w-40',
                  placeholder: 'Produto',
                  options: [{ label: 'Todos', value: '' }, ...productOptions],
                  onChange: (e: any) => setFilterParams((prev) => ({ ...prev, product: e?.value ? e : null })),
                  onInputChange: setSearchProduct,
                  value: filterParams.product,
                },
              ]}
            />

            <DatePickerWithRange date={date} setDate={(date) => setFilterParams((params) => ({ ...params, date }))} />
          </div>

          <Link
            to={priceSurveysIsPending ? '' : '/price-survey/create'}
            className={priceSurveysIsPending ? 'cursor-default' : ''}
          >
            <Button
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              data-testid="create_company_priceSurvey"
              disabled={priceSurveysIsPending}
              operationKlassToCheckPermission="Client::PriceSurveys::Create"
            >
              Criar pesquisa
            </Button>
          </Link>
        </div>

        <Table
          className="max-h-[calc(100vh-168px)]"
          rows={priceSurveys}
          columns={priceSurveyColumns}
          isLoadingData={priceSurveysIsPending}
          page={filterParams.page || 1}
          totalPages={meta.total_pages}
          totalCount={meta.total}
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          hasPagination
          rowEmptyValue="--"
        />
      </div>
    </ErrorMessageWrapper>
  )
}

export default List
